import React, { useState, useEffect } from 'react';
import { FiSearch, FiX, FiAlertCircle } from 'react-icons/fi';
import './DestinationsPage.css';
import { useNavigate } from 'react-router-dom';

// --- Breadcrumb component ---
const Breadcrumb = ({ items }) => (
  <nav className="breadcrumb">
    {items.map((item, index) => (
      <span key={index}>
        {index > 0 && ' / '}
        {item.href ? <a href={item.href}>{item.label}</a> : <span>{item.label}</span>}
      </span>
    ))}
  </nav>
);

// --- Destination Card (with badge, location, title, description, and explore link) ---
const DestinationCard = ({ destination }) => {
  const navigate = useNavigate();
  const location = destination.city && destination.state
    ? `${destination.city.name}, ${destination.state.name}`
    : destination.state?.name || destination.city?.name || '';

  return (
    <div className="destination-card1" onClick={() => navigate(`/destination-wedding-packages/${destination?.id}`)}>
      <div className="card-image-wrapper">
        <img
          src={destination.featured_photo_url || destination.banner_url}
          alt={destination.name}
          className="destination-card-img"
        />
        <div className="card-badge">
          {destination.packages_count || 0} Package{destination.packages_count !== 1 ? 's' : ''}
        </div>
      </div>
      <div className="destination-card-content">
        <p className="card-location1">{location}</p>
        <h3 className="card-title1">{destination.name}</h3>
        <p className="card-description1">
          {destination.short_description || 'No description available'}
        </p>
        <div className="card-footer">
          <span className="explore-link">Explore Venue →</span>
        </div>
      </div>
    </div>
  );
};

// --- Filters Component (with State and City dropdowns) ---
const DestinationFilters = ({ filters, onFilterChange, onClear, states, cities }) => {
  // cities is the list of cities filtered by selected state (or all cities)
  return (
    <div className="filters-container">
      <h3>Filters</h3>

      <div className="filter-group">
        <label className="filter-label">State</label>
        <select
          value={filters.state || ''}
          onChange={(e) => onFilterChange({ ...filters, state: e.target.value, city: '' })}
          className="filter-select"
        >
          <option value="">All States</option>
          {states.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <label className="filter-label">City</label>
        <select
          value={filters.city || ''}
          onChange={(e) => onFilterChange({ ...filters, city: e.target.value })}
          className="filter-select"
          disabled={cities.length === 0}
        >
          <option value="">All Cities</option>
          {cities.map((city) => (
            <option key={city} value={city}>
              {city}
            </option>
          ))}
        </select>
      </div>

      <button onClick={onClear} className="btn-outline">
        Clear Filters
      </button>
    </div>
  );
};

// --- Search Component (unchanged) ---
const DestinationSearch = ({ filters, onSearch, onClearSearch }) => {
  const [searchValue, setSearchValue] = useState(filters.search || '');

  useEffect(() => {
    setSearchValue(filters.search || '');
  }, [filters.search]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch(searchValue);
  };

  const handleClear = () => {
    setSearchValue('');
    onClearSearch();
  };

  return (
    <form onSubmit={handleSubmit} className="search-form">
      <div className="search-input-wrapper">
        <FiSearch className="search-icon" />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search destinations, states or cities"
          className="search-input"
        />
        {searchValue && (
          <button type="button" onClick={handleClear} className="search-clear-btn" aria-label="Clear search">
            <FiX />
          </button>
        )}
      </div>
      <button type="submit" className="btn-primary">Search</button>
    </form>
  );
};

// --- List Component (unchanged) ---
const DestinationList = ({ destinations, loading }) => {
  if (loading) {
    return (
      <div className="list-grid">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="skeleton-card"></div>
        ))}
      </div>
    );
  }

  if (destinations.length === 0) {
    return (
      <div className="empty-state">
        <FiAlertCircle className="empty-icon" />
        <h3>No Destinations Found</h3>
        <p>Try adjusting your filters or search terms</p>
      </div>
    );
  }

  return (
    <div className="list-grid">
      {destinations.map((dest) => (
        <DestinationCard key={dest.id} destination={dest} />
      ))}
    </div>
  );
};

// --- Main Page Component ---
export default function DestinationsPage() {
  const [filters, setFilters] = useState({ state: '', city: '', search: '' });
  const [allDestinations, setAllDestinations] = useState([]);
  const [filteredDestinations, setFilteredDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  

  // Available states and cities for filter dropdowns
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]); // will be computed based on selected state

  // Fetch data from API
  useEffect(() => {
    const fetchDestinations = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('https://admin.jayrajgroup.com/api/wedding/destinations');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        if (result.status && result.data) {
          setAllDestinations(result.data);
          // Extract unique state names
          const stateNames = [...new Set(result.data.map(d => d.state?.name).filter(Boolean))];
          setStates(stateNames);
          setFilteredDestinations(result.data);
        } else {
          throw new Error(result.message || 'Failed to fetch data');
        }
      } catch (err) {
        setError(err.message);
        setFilteredDestinations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDestinations();
  }, []);

  // Update city list whenever selected state changes or destinations change
  useEffect(() => {
    let cityNames = [];
    if (filters.state) {
      // Only cities from the selected state
      cityNames = allDestinations
        .filter(d => d.state?.name === filters.state)
        .map(d => d.city?.name)
        .filter(Boolean);
    } else {
      // All cities from all destinations
      cityNames = allDestinations
        .map(d => d.city?.name)
        .filter(Boolean);
    }
    // Unique and sorted
    const uniqueCities = [...new Set(cityNames)].sort();
    setCities(uniqueCities);
    // If the currently selected city is not in the new list, reset it
    if (filters.city && !uniqueCities.includes(filters.city)) {
      setFilters(prev => ({ ...prev, city: '' }));
    }
  }, [filters.state, allDestinations]);

  // Apply client‑side filtering whenever filters or allDestinations change
  useEffect(() => {
    const applyFilters = () => {
      let results = [...allDestinations];

      if (filters.state) {
        results = results.filter(d => d.state?.name === filters.state);
      }

      if (filters.city) {
        results = results.filter(d => d.city?.name === filters.city);
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        results = results.filter(d =>
          d.name.toLowerCase().includes(searchLower) ||
          d.city?.name?.toLowerCase().includes(searchLower) ||
          d.state?.name?.toLowerCase().includes(searchLower) ||
          d.short_description?.toLowerCase().includes(searchLower)
        );
      }

      setFilteredDestinations(results);
    };

    applyFilters();
  }, [filters, allDestinations]);

  // Handlers
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({ state: '', city: '', search: '' });
  };

  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
  };

  const handleClearSearch = () => {
    setFilters(prev => ({ ...prev, search: '' }));
  };

  // Error display
  if (error) {
    return (
      <div className="page-wrapper">
        <div className="container-custom">
          <div className="empty-state">
            <FiAlertCircle className="empty-icon" />
            <h3>Error loading destinations</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="container-custom">
        <Breadcrumb items={[{ label: 'Home', href: '/destination-wedding' }, { label: 'Destinations' }]} />

        <div className="page-header">
          <h1 className="section-title">Explore Our Destinations</h1>
          <p className="section-subtitle1">
            Discover the perfect destination for your wedding or travel experience
          </p>
        </div>

        <div className="page-grid">
          <aside className="filters-sidebar">
            <DestinationFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClear={handleClearFilters}
              states={states}
              cities={cities}
            />
          </aside>

          <main className="results-area">
            <DestinationSearch
              filters={filters}
              onSearch={handleSearch}
              onClearSearch={handleClearSearch}
            />
            <DestinationList destinations={filteredDestinations} loading={loading} />
          </main>
        </div>
      </div>
    </div>
  );
}